import React from 'react';
import { useTimer } from '../contexts/TimerContext.tsx';
import { PomodoroMethod } from '../methods/PomodoroMethod.ts';
import type { Settings } from '../types';
import './UpcomingSessions.css';

interface UpcomingSessionsProps {
  settings: Pick<Settings, 'workDuration' | 'shortBreakDuration' | 'longBreakDuration' | 'longBreakInterval'>;
}

const SESSION_LABELS: Record<string, string> = {
  pomodoro: 'Work',
  shortBreak: 'Short Break',
  longBreak: 'Long Break',
};

const SESSION_COLORS: Record<string, string> = {
  pomodoro: 'var(--kf-work)',
  shortBreak: 'var(--kf-break)',
  longBreak: 'var(--kf-long-break)',
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const UpcomingSessions: React.FC<UpcomingSessionsProps> = ({ settings }) => {
  const { currentTab, pomodoroCount, timeLeft, initialTimeLeft } = useTimer();

  const progress = initialTimeLeft > 0
    ? Math.min(100, ((initialTimeLeft - timeLeft) / initialTimeLeft) * 100)
    : 0;

  const currentColor = SESSION_COLORS[currentTab] ?? SESSION_COLORS.pomodoro;

  const queue = PomodoroMethod.buildQueue({
    workDuration: settings.workDuration,
    shortBreakDuration: settings.shortBreakDuration,
    longBreakDuration: settings.longBreakDuration,
    longBreakInterval: settings.longBreakInterval,
    pomodoroCount,
    currentTab,
  }).slice(0, 4);

  return (
    <div className="upcoming-sessions">
      <h3 className="upcoming-title">Up Next</h3>
      <div className="sessions-ribbon">
        <div
          className="ribbon-block ribbon-block--current"
          style={{
            '--session-color': currentColor,
            '--progress': `${progress}%`,
          } as React.CSSProperties}
        >
          <div className="ribbon-block__fill" />
          <div className="ribbon-block__content">
            <span className="ribbon-block__label">{SESSION_LABELS[currentTab] ?? 'Session'}</span>
            <span className="ribbon-block__duration">{formatDuration(initialTimeLeft)}</span>
          </div>
        </div>
        {queue.map((session, index) => (
          <div
            key={index}
            className="ribbon-block"
            style={{
              '--session-color': SESSION_COLORS[session.type] ?? SESSION_COLORS.pomodoro,
              '--fade-opacity': index >= 2 ? Math.max(0.2, 1 - (index - 1) * 0.35) : 1,
            } as React.CSSProperties}
          >
            <div className="ribbon-block__content">
              <span className="ribbon-block__label">{SESSION_LABELS[session.type]}</span>
              <span className="ribbon-block__duration">{formatDuration(session.durationSeconds)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UpcomingSessions;
